import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    createReviewerProfileChangeRequestApi,
    fetchReviewerProfilesOverviewApi,
    setDefaultReviewerProfileApi,
} from '../../http/userAPI';

const amazonSites = [
    { code: 'US', url: 'https://www.amazon.com', label: 'Amazon USA' },
    { code: 'UK', url: 'https://www.amazon.co.uk', label: 'Amazon UK' },
    { code: 'CA', url: 'https://www.amazon.ca', label: 'Amazon Canada' },
    { code: 'DE', url: 'https://www.amazon.de', label: 'Amazon Germany' },
    { code: 'FR', url: 'https://www.amazon.fr', label: 'Amazon France' },
    { code: 'IT', url: 'https://www.amazon.it', label: 'Amazon Italy' },
    { code: 'ES', url: 'https://www.amazon.es', label: 'Amazon Spain' },
    { code: 'JP', url: 'https://www.amazon.co.jp', label: 'Amazon Japan' },
    { code: 'AU', url: 'https://www.amazon.com.au', label: 'Amazon Australia' },
    { code: 'IN', url: 'https://www.amazon.in', label: 'Amazon India' },
    { code: 'MX', url: 'https://www.amazon.com.mx', label: 'Amazon Mexico' },
    { code: 'BR', url: 'https://www.amazon.com.br', label: 'Amazon Brazil' },
];

const audibleSites = [
    { code: 'AUS', url: 'https://www.audible.com', label: 'Audible USA' },
    { code: 'AUK', url: 'https://www.audible.co.uk', label: 'Audible UK' },
];

const emptyForm = {
    label: '',
    amazon_name: '',
    amazon_country: '',
    audible_name: '',
    audible_country: '',
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

    return `${day} ${month} ${year} ${time}`;
};

const marketplaceLabel = (sites, value) => (
    sites.find((site) => site.url === value)?.label || value || '-'
);

const buildPayloadSummary = (payload = {}) => {
    const parts = [
        payload.label ? `Label: ${payload.label}` : null,
        payload.amazon_name ? `Amazon: ${payload.amazon_name}` : null,
        payload.amazon_country ? marketplaceLabel(amazonSites, payload.amazon_country) : null,
        payload.audible_name ? `Audible: ${payload.audible_name}` : null,
        payload.audible_country ? marketplaceLabel(audibleSites, payload.audible_country) : null,
    ].filter(Boolean);

    return parts.length ? parts.join(' | ') : '-';
};

const buildProfileSetSummary = (profile, ordinalMap = {}) => {
    if (!profile) return '-';

    const setLabel = `Set ${ordinalMap[profile.id] || '?'}`;
    const amazonName = profile.amazon_name || '-';
    const amazonCountry = marketplaceLabel(amazonSites, profile.amazon_country);
    const audibleName = profile.audible_name || '-';
    const audibleCountry = marketplaceLabel(audibleSites, profile.audible_country);

    return `${setLabel}: Amazon reviewer "${amazonName}" / ${amazonCountry}; Audible reviewer "${audibleName}" / ${audibleCountry}`;
};

const tariffLimitToastMessage = 'Active set limit reached for your tariff.';
const tariffLimitMessage = 'Active set limit reached for your tariff. FREE: 1 set, ESSENTIAL: 2 sets, PREMIUM: 3 sets, MAXIMUM: 5 sets.';
const greenSwitchStyle = {
    backgroundColor: '#198754',
    borderColor: '#198754',
};

const ReviewerProfilesPanel = ({ tariffPlan }) => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [hideArchived, setHideArchived] = useState(true);
    const [overview, setOverview] = useState({
        profiles: [],
        changeRequests: [],
        limit: 1,
        activeCount: 0,
        tariffPlan,
    });
    const [requestType, setRequestType] = useState('CREATE');
    const [targetProfileId, setTargetProfileId] = useState('');
    const [form, setForm] = useState(emptyForm);
    const [makingDefaultId, setMakingDefaultId] = useState(null);

    const activeProfiles = useMemo(
        () => overview.profiles.filter((profile) => String(profile.status || '').toUpperCase() === 'ACTIVE'),
        [overview.profiles]
    );

    const displayedProfiles = useMemo(
        () => (hideArchived
            ? overview.profiles.filter((profile) => String(profile.status || '').toUpperCase() === 'ACTIVE')
            : overview.profiles
        ).slice().sort((a, b) => Number(b.id || 0) - Number(a.id || 0)),
        [overview.profiles, hideArchived]
    );

    const profileOrdinalMap = useMemo(() => (
        overview.profiles.reduce((map, profile, index) => {
            map[profile.id] = index + 1;
            return map;
        }, {})
    ), [overview.profiles]);

    const activeDefaultProfile = useMemo(
        () => activeProfiles.find((profile) => profile.is_default) || null,
        [activeProfiles]
    );

    const hasRealActiveProfile = useMemo(
        () => activeProfiles.some((profile) => (
            String(profile.amazon_name || '').trim() &&
            String(profile.amazon_country || '').trim()
        )),
        [activeProfiles]
    );

    const selectedProfile = useMemo(
        () => overview.profiles.find((profile) => Number(profile.id) === Number(targetProfileId)),
        [overview.profiles, targetProfileId]
    );

    const targetProfiles = useMemo(
        () => requestType === 'ARCHIVE'
            ? activeProfiles.filter((profile) => !profile.is_default)
            : activeProfiles,
        [activeProfiles, requestType]
    );

    const buildRequestProfileSummary = (request) => {
        if (request?.request_type === 'CREATE' && request?.requested_payload) {
            return buildPayloadSummary(request.requested_payload);
        }

        const targetProfile = request?.targetProfile ||
            overview.profiles.find((profile) => Number(profile.id) === Number(request?.target_profile_id));

        if (targetProfile) {
            return buildProfileSetSummary(targetProfile, profileOrdinalMap);
        }

        if (request?.requested_payload) {
            return buildProfileSetSummary(
                {
                    id: request.requested_payload.target_profile_id || request.target_profile_id,
                    ...request.requested_payload,
                },
                profileOrdinalMap
            );
        }

        return '-';
    };

    const canCreate = activeProfiles.length < Number(overview.limit || 1) || !hasRealActiveProfile;
    const showPayloadForm = requestType === 'CREATE' || requestType === 'UPDATE';

    const loadOverview = async () => {
        setLoading(true);
        try {
            const data = await fetchReviewerProfilesOverviewApi();
            const profiles = (data?.profiles || []).slice().sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
            setOverview({
                profiles,
                changeRequests: data?.changeRequests || [],
                limit: data?.limit || 1,
                activeCount: data?.activeCount || 0,
                tariffPlan: data?.tariffPlan || tariffPlan,
            });
        } catch (error) {
            const message = error?.response?.data?.message || 'Failed to load reviewer profiles.';
            toast.error(message, { position: 'bottom-center', autoClose: 3000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOverview();
    }, []);

    useEffect(() => {
        if (requestType === 'UPDATE' && selectedProfile) {
            setForm({
                label: selectedProfile.label || '',
                amazon_name: selectedProfile.amazon_name || '',
                amazon_country: selectedProfile.amazon_country || '',
                audible_name: selectedProfile.audible_name || '',
                audible_country: selectedProfile.audible_country || '',
            });
            return;
        }

        if (requestType === 'CREATE') {
            setForm(emptyForm);
        }
    }, [requestType, selectedProfile]);

    useEffect(() => {
        if (requestType === 'CREATE' || !targetProfileId) return;

        const stillSelectable = targetProfiles.some((profile) => Number(profile.id) === Number(targetProfileId));
        if (!stillSelectable) {
            setTargetProfileId('');
        }
    }, [requestType, targetProfileId, targetProfiles]);

    const handleFieldChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const stopOuterFormSubmit = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const handleSubmit = async (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        if (requestType === 'CREATE' && !canCreate) {
            toast.error(tariffLimitToastMessage, {
                position: 'bottom-center',
                autoClose: 3000,
            });
            return;
        }

        if (requestType !== 'CREATE' && !targetProfileId) {
            toast.error('Select reviewer set first.', { position: 'bottom-center', autoClose: 3000 });
            return;
        }

        if (requestType === 'ARCHIVE' && selectedProfile?.is_default) {
            toast.error('Default reviewer set cannot be archived. Make another active set default first.', {
                position: 'bottom-center',
                autoClose: 3000,
            });
            return;
        }

        if (showPayloadForm && (!form.amazon_name.trim() || !form.amazon_country.trim())) {
            toast.error('Amazon reviewer name and country are required.', {
                position: 'bottom-center',
                autoClose: 3000,
            });
            return;
        }

        setSubmitting(true);
        try {
            const response = await createReviewerProfileChangeRequestApi({
                request_type: requestType,
                target_profile_id: requestType === 'CREATE' ? null : Number(targetProfileId),
                requested_payload: showPayloadForm ? form : {},
            });
            toast.success(
                response?.autoApproved
                    ? 'First reviewer profile approved automatically.'
                    : 'Reviewer profile request submitted.',
                {
                position: 'bottom-center',
                autoClose: 3000,
                }
            );
            setRequestType('CREATE');
            setTargetProfileId('');
            setForm(emptyForm);
            await loadOverview();
        } catch (error) {
            const message = error?.response?.data?.message || 'Failed to submit reviewer profile request.';
            toast.error(message, { position: 'bottom-center', autoClose: 3000 });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSetDefault = async (profileId) => {
        setMakingDefaultId(profileId);
        try {
            await setDefaultReviewerProfileApi(profileId);
            toast.success('Default reviewer set updated.', {
                position: 'bottom-center',
                autoClose: 3000,
            });
            await loadOverview();
        } catch (error) {
            const message = error?.response?.data?.message || 'Failed to update default reviewer set.';
            toast.error(message, { position: 'bottom-center', autoClose: 3000 });
        } finally {
            setMakingDefaultId(null);
        }
    };

    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div>
                    <div className="mb-1 fw-semibold">Reviewer profile sets</div>
                    <div className="text-muted small mb-0">
                        {overview.tariffPlan || tariffPlan || 'FREE'} plan: {overview.activeCount} / {overview.limit} active set(s).
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="form-check form-switch mb-0">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="hideArchivedReviewerProfiles"
                            checked={hideArchived}
                            onChange={(event) => setHideArchived(event.target.checked)}
                            style={hideArchived ? greenSwitchStyle : undefined}
                        />
                        <label className="form-check-label small text-muted" htmlFor="hideArchivedReviewerProfiles">
                            Hide archived
                        </label>
                    </div>
                    {loading && <Spinner animation="border" size="sm" />}
                </div>
            </div>

            <div className="table-responsive border rounded mb-3">
                <table className="table table-sm align-middle mb-0 small">
                    <thead className="table-light">
                    <tr>
                        <th className="fw-semibold">Set</th>
                        <th className="fw-semibold">Amazon reviewer name</th>
                        <th className="fw-semibold">Amazon country</th>
                        <th className="fw-semibold">Audible reviewer name</th>
                        <th className="fw-semibold">Audible country</th>
                        <th className="fw-semibold text-center">Status</th>
                        <th className="fw-semibold text-center">Default</th>
                    </tr>
                    </thead>
                    <tbody>
                    {displayedProfiles.length === 0 ? (
                        <tr>
                            <td colSpan="7" className="text-muted text-center py-3">
                                No reviewer sets found yet.
                            </td>
                        </tr>
                    ) : displayedProfiles.map((profile) => {
                        const profileStatus = String(profile.status || '').toUpperCase();
                        const isActive = profileStatus === 'ACTIVE';
                        const isActiveDefault = isActive && activeDefaultProfile?.id === profile.id;

                        return (
                        <tr key={profile.id} className={isActiveDefault ? 'table-success' : ''}>
                            <td>
                                <span className="fw-semibold">Set {profileOrdinalMap[profile.id] || '-'}</span>
                            </td>
                            <td>{profile.amazon_name || '-'}</td>
                            <td>{marketplaceLabel(amazonSites, profile.amazon_country)}</td>
                            <td>{profile.audible_name || '-'}</td>
                            <td>{marketplaceLabel(audibleSites, profile.audible_country)}</td>
                            <td className="text-center">
                                <span className={`badge ${isActive ? 'text-bg-primary' : 'text-bg-secondary'}`}>
                                    {profileStatus || '-'}
                                </span>
                            </td>
                            <td className="text-center">
                                {isActiveDefault ? (
                                    <span className="badge text-bg-success">Default</span>
                                ) : isActive ? (
                                    <Button
                                        type="button"
                                        variant="outline-success"
                                        className="btn-sm py-0 px-2"
                                        disabled={loading || submitting || makingDefaultId === profile.id}
                                        onClick={() => handleSetDefault(profile.id)}
                                    >
                                        {makingDefaultId === profile.id ? 'Saving...' : 'Set default'}
                                    </Button>
                                ) : (
                                    <span className="text-muted small">-</span>
                                )}
                            </td>
                        </tr>
                    );
                    })}
                    </tbody>
                </table>
            </div>

            <div className="border border-secondary rounded p-3 mb-3" onKeyDown={stopOuterFormSubmit}>
                <Row className="g-2 align-items-end mb-3">
                    <Col xs={12} md={2}>
                        <label className="form-label text-muted small mb-1 text-nowrap">Request type</label>
                        <select
                            className="form-select form-select-sm border-secondary"
                            value={requestType}
                            onChange={(event) => {
                                setRequestType(event.target.value);
                                setTargetProfileId('');
                            }}
                        >
                            <option value="CREATE" disabled={!canCreate}>Add new set</option>
                            <option value="UPDATE">Edit selected set</option>
                            <option value="ARCHIVE" disabled={activeProfiles.filter((profile) => !profile.is_default).length === 0}>
                                Archive selected set
                            </option>
                        </select>
                    </Col>
                    {requestType !== 'CREATE' && (
                        <Col xs={12} md={2}>
                            <label className="form-label text-muted small mb-1 text-nowrap">Reviewer set</label>
                            <select
                                className="form-select form-select-sm border-secondary"
                                value={targetProfileId}
                                onChange={(event) => setTargetProfileId(event.target.value)}
                            >
                                <option value="">Select set</option>
                                {targetProfiles.map((profile) => (
                                    <option key={profile.id} value={profile.id}>
                                        Set {profileOrdinalMap[profile.id] || '-'} {activeDefaultProfile?.id === profile.id ? '(default)' : ''}
                                    </option>
                                ))}
                            </select>
                            {requestType === 'ARCHIVE' && targetProfiles.length === 0 && (
                                <div className="text-muted small mt-1">
                                    Default set cannot be archived. Make another active set default first.
                                </div>
                            )}
                        </Col>
                    )}
                    {showPayloadForm && (
                        <>
                            <Col xs={12}  md={requestType === 'CREATE' ? 3 : 2}>
                                <label className="form-label text-muted small mb-1 text-nowrap">Amazon reviewer name</label>
                                <input
                                    className="form-control form-control-sm border-secondary"
                                    name="amazon_name"
                                    value={form.amazon_name}
                                    onChange={handleFieldChange}
                                />
                            </Col>
                            <Col xs={12} md={2}>
                                <label className="form-label text-muted small mb-1 text-nowrap">Amazon country</label>
                                <select
                                    className="form-select form-select-sm border-secondary"
                                    name="amazon_country"
                                    value={form.amazon_country}
                                    onChange={handleFieldChange}
                                >
                                    <option value="">Select country</option>
                                    {amazonSites.map((site) => (
                                        <option key={site.code} value={site.url}>{site.label}</option>
                                    ))}
                                </select>
                            </Col>
                            <Col xs={12} md={requestType === 'CREATE' ? 3 : 2}>
                                <label className="form-label text-muted small mb-1 text-nowrap">Audible reviewer name</label>
                                <input
                                    className="form-control form-control-sm border-secondary"
                                    name="audible_name"
                                    value={form.audible_name}
                                    onChange={handleFieldChange}
                                />
                            </Col>
                            <Col xs={12} md={2}>
                                <label className="form-label text-muted small mb-1 text-nowrap">Audible country</label>
                                <select
                                    className="form-select form-select-sm border-secondary"
                                    name="audible_country"
                                    value={form.audible_country}
                                    onChange={handleFieldChange}
                                >
                                    <option value="">Select country</option>
                                    {audibleSites.map((site) => (
                                        <option key={site.code} value={site.url}>{site.label}</option>
                                    ))}
                                </select>
                            </Col>
                        </>
                    )}
                    <Col xs={12} md={requestType === 'CREATE' ? 9 : 4}>
                        {!canCreate && requestType === 'CREATE' && (
                            <div className="text-danger small">
                                {tariffLimitMessage}
                            </div>
                        )}
                    </Col>
                </Row>



                <div className="d-flex justify-content-end">
                    <Button
                        type="button"
                        variant="success"
                        size="sm"
                        disabled={submitting || loading}
                        onClick={handleSubmit}
                    >
                        {submitting ? 'Submitting...' : 'Submit request'}
                    </Button>
                </div>
            </div>

            <div className="mb-2 fw-semibold">Request history</div>
            <div className="table-responsive border rounded">
                <table className="table table-sm align-middle mb-0 small">
                    <thead className="table-light">
                    <tr>
                        <th className="fw-semibold">Operation</th>
                        <th className="fw-semibold">Date</th>
                        <th className="fw-semibold">Profile set</th>
                        <th className="fw-semibold">Staff comment</th>
                        <th className="fw-semibold">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    {overview.changeRequests.length === 0 ? (
                        <tr>
                            <td colSpan="5" className="text-muted text-center py-3">
                                No requests yet.
                            </td>
                        </tr>
                    ) : overview.changeRequests.map((request) => (
                        <tr key={request.id}>
                            <td className="">{request.request_type}</td>
                            <td className="text-nowrap">{formatDate(request.created_at)}</td>
                            <td className="">{buildRequestProfileSummary(request)}</td>
                            <td>{request.staff_comment || '-'}</td>
                            <td>
                                <span className={`badge ${
                                    request.status === 'APPROVED'
                                        ? 'text-bg-success'
                                        : request.status === 'REJECTED'
                                            ? 'text-bg-danger'
                                            : 'text-bg-warning'
                                }`}>
                                    {request.status}
                                </span>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ReviewerProfilesPanel;
